import { Extension } from "@tiptap/core";
import { Plugin } from "@tiptap/pm/state";

import { transformInlineExpressionsToText } from "@/components/tiptap/utils/transformInlineExpressionsToText";
import { WrapperNodeModel } from "@/models/nodes/WrapperNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { Node } from "@tiptap/pm/model";

type TransformExpressionsToTextOptions = {
  types: string[];
  doc?: DocumentModel;
  shouldUpdate: (doc: DocumentModel, newNode: Node) => boolean;
};

const TRANSFORM_TYPES = ["documentTitle", "heading"];

/**
 * This extension transforms expressions to text in these cases:
 * - A node is updated (e.g., user pastes content containing inline expressions)
 * - A new node is created (e.g., user converts a paragraph with expressions to a heading)
 */
export const TransformExpressionsToText = Extension.create<TransformExpressionsToTextOptions>({
  name: "transformExpressionsToText",

  addOptions() {
    return {
      ...this.parent?.(),
      types: TRANSFORM_TYPES,
      shouldUpdate: (doc, newNode) => {
        if (!doc) {
          return false;
        }
        let hasExpressions = false;

        newNode.descendants((node) => {
          if (node.type.name === "inlineExpression") {
            const nodeId = node.attrs.nodeId as string;
            const expressionNode = doc.nodes.get(nodeId);

            /**
             * It's a hack that handles pasted partial expressions. Should be fixed in `CopyPaste`.
             * https://linear.app/corca-research/issue/D-1821/pasting-partial-expressions-adds-unnecessary-transaction-step
             */
            if (expressionNode?.is(WrapperNodeModel) && !expressionNode.inlineNode?.isEmpty) {
              hasExpressions = true;
              return false;
            }
          }

          return false;
        });

        return hasExpressions;
      },
    };
  },

  addProseMirrorPlugins() {
    const { doc, types, shouldUpdate } = this.options;

    return [
      new Plugin({
        appendTransaction: (transactions, oldState, newState) => {
          const hasDocChanges =
            transactions.some((transaction) => transaction.docChanged) &&
            !oldState.doc.eq(newState.doc);
          const isCollabTransaction = transactions.find((tr) => tr.getMeta("y-sync$"));

          if (isCollabTransaction || !hasDocChanges) {
            return null;
          }

          let { tr } = newState;

          newState.doc.descendants((node, pos) => {
            if (!types.includes(node.type.name)) {
              return false;
            }

            if (doc && shouldUpdate(doc, node)) {
              tr = transformInlineExpressionsToText({ node, pos }, newState, doc);
            }

            return false;
          });

          if (tr.steps.length > 0) {
            return tr;
          }

          return null;
        },
      }),
    ];
  },
});
